/**
 * Per-device memory of the last successful MFA method for a user.
 * Shared across portals via a cookie on `.qixit.com` (and host-only on localhost,
 * which browsers share across ports). localStorage is kept as a same-origin mirror.
 *
 * `phone` = passkey via hybrid/QR (distinct from local `passkey` so we do not
 * auto-start Windows Security before the QR sheet).
 */

const STORAGE_PREFIX = 'summa.mfa.lastMethod.';
const COOKIE_NAME = 'summa_mfa_last_methods';
const COOKIE_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;
const MAX_USERS_IN_COOKIE = 20;

const REMEMBERABLE = new Set(['passkey', 'phone', 'totp', 'backup_code', 'email_recovery']);

type MethodMap = Record<string, string>;

function getCookieDomain(): string {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '';
  if (hostname.includes('qixit.com')) return '.qixit.com';
  return '';
}

function readCookie(name: string): string | null {
  const parts = document.cookie.split(';');
  for (const part of parts) {
    const [cookieName, ...rest] = part.trim().split('=');
    if (cookieName === name) {
      try {
        return decodeURIComponent(rest.join('='));
      } catch {
        return rest.join('=');
      }
    }
  }
  return null;
}

function writeCookie(name: string, value: string, maxAgeMs: number): void {
  const expires = new Date(Date.now() + maxAgeMs).toUTCString();
  const domain = getCookieDomain();
  let cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
  if (domain) cookie += `; domain=${domain}`;
  cookie += window.location.protocol === 'https:' ? '; Secure; SameSite=Lax' : '; SameSite=Lax';
  document.cookie = cookie;
}

function parseMethodMap(raw: string | null): MethodMap {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: MethodMap = {};
    for (const [userId, method] of Object.entries(parsed as Record<string, unknown>)) {
      const m = String(method || '').trim().toLowerCase();
      if (userId && REMEMBERABLE.has(m)) out[userId] = m;
    }
    return out;
  } catch {
    return {};
  }
}

function readSharedMap(): MethodMap {
  return parseMethodMap(readCookie(COOKIE_NAME));
}

function writeSharedMap(map: MethodMap): void {
  const entries = Object.entries(map);
  const trimmed =
    entries.length <= MAX_USERS_IN_COOKIE
      ? map
      : Object.fromEntries(entries.slice(entries.length - MAX_USERS_IN_COOKIE));
  writeCookie(COOKIE_NAME, JSON.stringify(trimmed), COOKIE_MAX_AGE_MS);
}

function normalizeUserId(userId: string): string {
  return (userId || '').trim().toLowerCase();
}

export function readLastMfaMethod(userId: string): string | null {
  if (typeof window === 'undefined' || !userId) return null;
  const id = normalizeUserId(userId);

  try {
    const fromCookie = readSharedMap()[id];
    if (fromCookie && REMEMBERABLE.has(fromCookie)) return fromCookie;
  } catch {
    /* ignore */
  }

  try {
    const fromLocal = (window.localStorage.getItem(STORAGE_PREFIX + userId) || '').trim().toLowerCase();
    if (REMEMBERABLE.has(fromLocal)) {
      writeLastMfaMethod(userId, fromLocal);
      return fromLocal;
    }
  } catch {
    /* ignore */
  }

  return null;
}

export function writeLastMfaMethod(userId: string, method: string): void {
  if (typeof window === 'undefined' || !userId) return;
  const normalized = (method || '').trim().toLowerCase();
  if (!REMEMBERABLE.has(normalized)) return;
  const id = normalizeUserId(userId);

  try {
    const map = readSharedMap();
    map[id] = normalized;
    writeSharedMap(map);
  } catch {
    /* ignore */
  }

  try {
    window.localStorage.setItem(STORAGE_PREFIX + userId, normalized);
  } catch {
    /* private mode / quota — ignore */
  }
}

/**
 * Pick the method to open first: last-used on this device if still enrolled,
 * otherwise the server preferred method.
 * `phone` requires an enrolled passkey (hybrid/QR path).
 */
export function resolveOpeningMfaMethod(
  userId: string,
  availableMethods: string[],
  serverPreferred?: string | null
): string | null {
  const available = new Set(availableMethods.map((m) => m.toLowerCase()));
  const last = readLastMfaMethod(userId);
  if (last === 'phone' && available.has('passkey')) return 'phone';
  if (last && last !== 'phone' && available.has(last)) return last;

  const preferred = (serverPreferred || '').trim().toLowerCase();
  if (preferred && available.has(preferred)) return preferred;

  if (available.has('passkey')) return 'passkey';
  if (available.has('totp')) return 'totp';
  if (available.has('backup_code')) return 'backup_code';
  return null;
}
